/**
 * The two boundary questions the UI asks, kept apart on purpose (PRD FR28).
 *
 * They have **different subjects** and conflating them is what let the browser predict one
 * stage while the server wrote another:
 *
 *  - **Routing** — "where does this Draft land?" — is a property of the request's CREATOR.
 *    The server resolves it and stamps `requestorIsExternal` on `GET /requests/:id`, so
 *    the client mirrors that answer rather than re-deriving one.
 *  - **Display** — "may this person see the internal-only 'Configuration' label?" — is a
 *    property of the VIEWER, and is answered from the viewer's own roles.
 *
 * Both fail closed, but at opposite polarities: an unresolvable routing answer is
 * `external` (an unnecessary review is the cheap failure); an unresolvable display answer
 * is `hidden` (never reveal internal detail to someone we cannot identify).
 */

import { roleService } from '../services/auth/roleService';

import type { Request } from '../types';
import type { User } from '../types/auth';

/**
 * Whether we actually know this viewer's roles.
 *
 * `roleService.isExternalRequestor` and `roleService.isInternalUser` read **different**
 * attributes (`roles` vs `userType`) and are not complements. A bare
 * `!isExternalRequestor(user)` is therefore true when roles are simply missing, which
 * would REVEAL the internal-only label to an unidentified viewer — the opposite of what
 * `!isInternalUser(user)` used to do. This guard is what keeps the polarity fail-closed.
 */
export const hasLoadedViewerRoles = (user: User | null | undefined): boolean =>
  !!user && Array.isArray(user.roles) && user.roles.length > 0;

/**
 * Display predicate: may this viewer see CONFIGURATION's internal-only "Configuration"
 * label (PRD FR7)?
 *
 * Requires roles to be present AND to not include `External Requestor`. A viewer with no
 * roles loaded — cached identity, offline, mid-auth — gets the plain "In Progress" label.
 */
export const showsInternalStageDetail = (
  user: User | null | undefined,
): boolean =>
  hasLoadedViewerRoles(user) && !roleService.isExternalRequestor(user ?? null);

const normalizeEmail = (email?: string | null): string =>
  typeof email === 'string' ? email.trim().toLowerCase() : '';

/**
 * Routing predicate: does this request's Draft submit pass through the review gate?
 *
 * Prefers the server's stamped answer about the CREATOR. When it is absent — the list
 * cache, an offline read, or a lookup that faulted — falls back to the viewer's own role,
 * but ONLY when the viewer is the creator, because that is the one case where the viewer's
 * role answers a question about the creator. Anyone else opening someone's Draft fails
 * closed to external.
 */
export const resolveRequestorIsExternal = (
  request: Partial<Request> | null | undefined,
  user: User | null | undefined,
): boolean => {
  if (typeof request?.requestorIsExternal === 'boolean') {
    return request.requestorIsExternal;
  }

  const creatorEmail = normalizeEmail(request?.requestorEmail);
  const viewerEmail = normalizeEmail(user?.email);
  const viewerIsCreator =
    creatorEmail.length > 0 && creatorEmail === viewerEmail;

  if (viewerIsCreator && hasLoadedViewerRoles(user)) {
    return roleService.isExternalRequestor(user ?? null);
  }

  return true;
};
